import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  DateField,
  ReferenceField,
  ArrayField,
  Datagrid,
  FunctionField,
} from 'react-admin';
import { Chip, Box, Typography } from '@mui/material';

export const ProductShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="sku" />
      <TextField source="description" />
      
      <ReferenceField source="brandId" reference="brands" link="show">
        <TextField source="name" />
      </ReferenceField>
      
      <ReferenceField source="categoryId" reference="categories" link="show">
        <TextField source="name" />
      </ReferenceField>
      
      <NumberField source="price" options={{ style: 'currency', currency: 'USD' }} />
      <NumberField source="quantity" />
      <TextField source="status" />
      
      <FunctionField
        label="Product Images"
        render={(record: any) => (
          <Box>
            {record.images?.map((imageRelation: any, index: number) => {
              const media = imageRelation.media;
              if (!media) return null;
              
              // Use thumbnail if available, otherwise fallback to original
              const imageUrl = media.thumbnails?.find((t: any) => t.size === 'medium')?.url || media.url;
              
              return (
                <img 
                  key={index}
                  src={imageUrl} 
                  alt={`Product ${index + 1}`} 
                  style={{ maxWidth: 200, maxHeight: 200, objectFit: 'cover', margin: 4 }} 
                />
              );
            })}
          </Box>
        )}
      />
      
      <FunctionField
        label="Catalog Images"
        render={(record: any) => (
          <Box>
            {record.catalogImages?.map((imageRelation: any, index: number) => {
              const media = imageRelation.media;
              if (!media) return null;
              
              // Use thumbnail if available, otherwise fallback to original
              const imageUrl = media.thumbnails?.find((t: any) => t.size === 'medium')?.url || media.url;
              
              return (
                <img 
                  key={index}
                  src={imageUrl} 
                  alt={`Catalog ${index + 1}`} 
                  style={{ maxWidth: 200, maxHeight: 200, objectFit: 'cover', margin: 4 }} 
                />
              );
            })}
          </Box>
        )}
      />
      
      <ArrayField source="documents" label="Product Documents">
        <Datagrid>
          <TextField source="name" />
          <FunctionField
            label="Document"
            render={(record: any) => {
              if (record.media) {
                return (
                  <a href={record.media.url} target="_blank" rel="noopener noreferrer">
                    {record.media.filename || 'Download'}
                  </a>
                );
              }
              return 'No file';
            }}
          />
          <NumberField source="sortOrder" />
        </Datagrid>
      </ArrayField>
      
      <FunctionField
        label="Tags"
        render={(record: any) => (
          <Box>
            {record.tags?.map((tag: any) => (
              <Chip key={tag.id} label={tag.name} style={{ margin: 2 }} />
            ))}
          </Box>
        )}
      />
      
      <FunctionField
        label="Characteristics"
        render={(record: any) => (
          <Box>
            {record.characteristics?.map((char: any) => (
              <Typography key={char.id} variant="body2" style={{ margin: 2 }}>
                <strong>{char.characteristic.name}:</strong> {char.value} {char.characteristic.unit}
              </Typography>
            ))}
          </Box>
        )}
      />
      
      <FunctionField
        label="Attributes"
        render={(record: any) => (
          <Box>
            {record.attributes?.map((attr: any) => (
              <Typography key={attr.id} variant="body2" style={{ margin: 2 }}>
                <strong>{attr.attribute.name}:</strong> {attr.value}
              </Typography>
            ))}
          </Box>
        )}
      />
      
      <FunctionField
        label="Configurator Groups"
        render={(record: any) => (
          <Box>
            {record.configuratorGroups?.map((group: any) => (
              <Box key={group.id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'grey.300', borderRadius: 1 }}>
                <Typography variant="h6">{group.name}</Typography>
                {group.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {group.description}
                  </Typography>
                )}
                <Box sx={{ ml: 2 }}>
                  {group.options?.map((option: any) => (
                    <Box key={option.id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body1">
                        <strong>{option.name}</strong> - ${option.price}
                      </Typography>
                      {option.description && (
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                      {option.sku && (
                        <Typography variant="caption" color="text.secondary">
                          SKU: {option.sku}
                        </Typography>
                      )}
                      {option.imageId && option.media && (
                        <img 
                          src={option.media.thumbnails?.find((t: any) => t.type === 'W550')?.url || option.media.url} 
                          alt={option.name} 
                          style={{ maxWidth: 100, maxHeight: 100, objectFit: 'cover', marginTop: 4 }} 
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      />
      
      <DateField source="createdAt" />
      <DateField source="updatedAt" />
    </SimpleShowLayout>
  </Show>
);
